import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { api } from "../../../env";
import Loading from "../../../components/loading";
import { ChartState, toISODate } from "../stats/charts";

function getDates(tab) {
    const end = new Date();
    const start = new Date();
    if (tab === 'Yearly') {
        start.setDate(end.getDate() - 365);
    } else {
        start.setDate(end.getDate() - 180);
    }
    return { start: toISODate(start), end: toISODate(end) };
}

function getTrend(data) {
    if (data.length < 3) return null;
    const oneMonthAgo = data[data.length - 2];
    const twoMonthsAgo = data[data.length - 3];
    if (oneMonthAgo > twoMonthsAgo * 1.05) return "Trending up";
    if (oneMonthAgo < twoMonthsAgo * 0.95) return "Trending down";
    return "No trend";
}

export default function HomeEnrollmentsChart({ programId }) {
    const [tab, setTab] = useState('Monthly');
    const [chartState, setChartState] = useState(ChartState.LOADING);
    const [categories, setCategories] = useState([]);
    const [seriesData, setSeriesData] = useState([]);
    const trend = chartState === ChartState.SHOWING ? getTrend(seriesData) : null;

    useEffect(() => {
        if (!programId) return;
        setChartState(ChartState.LOADING);
        const controller = new AbortController();
        const { start, end } = getDates(tab);
        api.get(`programs/${programId}/timeseries/enrollments`, {
            signal: controller.signal,
            params: { startDate: start, endDate: end }
        }).then(res => {
            const data = res.data.data;
            setCategories(data.map(d => d.month));
            setSeriesData(data.map(d => (d.totalMentors || 0) + (d.totalMentees || 0)));
            setChartState(data.length === 0 ? ChartState.NO_DATA : ChartState.SHOWING);
        }).catch(() => setChartState(ChartState.ERROR));
        return () => controller.abort();
    }, [programId, tab]);

    const style = getComputedStyle(document.documentElement);
    const primaryColor = `rgb(${style.getPropertyValue('--primary-dark').trim()})`;

    const options = {
        chart: { type: 'area', height: 220, toolbar: { show: false }, sparkline: { enabled: false } },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } },
        colors: [primaryColor],
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { rotate: -30, style: { fontSize: '11px' } } },
        yaxis: { labels: { formatter: v => Math.round(v) } },
        tooltip: { y: { formatter: v => Math.round(v) } },
        grid: { borderColor: 'rgba(0,0,0,0.06)', padding: { left: 4, right: 4 } }
    };

    const tabLabel = tab === 'Monthly' ? 'Last 6 months' : 'Last 12 months';

    return (
        <div>
            <div className="flex space-between center v-spacer-xs">
                <p className="card-title">Enrolments</p>
                <div className="flex gap-xxs">
                    <button
                        className={tab === 'Monthly' ? 'chip primary-dark-chip' : 'chip neutral-chip'}
                        onClick={() => setTab('Monthly')}
                    >Monthly</button>
                    <button
                        className={tab === 'Yearly' ? 'chip primary-dark-chip' : 'chip neutral-chip'}
                        onClick={() => setTab('Yearly')}
                    >Yearly</button>
                </div>
            </div>
            {chartState === ChartState.LOADING && (
                <div className="flex justify-center center" style={{ height: 220 }}>
                    <Loading />
                </div>
            )}
            {chartState === ChartState.ERROR && (
                <div className="error-card standard-padding-s">
                    <p className="card-title error">Error fetching data</p>
                </div>
            )}
            {chartState === ChartState.NO_DATA && (
                <div className="dashed-card standard-padding-s">
                    <p className="card-title">No enrolment data yet</p>
                </div>
            )}
            {chartState === ChartState.SHOWING && (
                <ReactApexChart
                    options={options}
                    series={[{ name: 'Enrolments', data: seriesData }]}
                    type="area"
                    height={220}
                />
            )}
            <p className="card-label neutral vi-spacer-xs">{tabLabel} {trend ? `${trend}` : ""}</p>
        </div>
    );
}
